/**
 * 概要:
 * このモジュールは、Google Fontsなどutoniq-appで標準利用していないカスタムフォントの適用を行うためのユーティリティである。
 * カスタムデザイン対応などでカスタムフォントが必要になった場合に利用する。
 * カスタムフォントを追加する場合は、このモジュールをカスタマイズすること。
 *
 * 目的:
 * カスタムフォントの取得やアプリへの適用、カスタムフォントの一元管理を目的とする
 * - フォントのheadタグへの挿入や、classNameの自動生成を簡便化
 * - 複数のカスタムフォントを一元的に管理し、コンポーネントとして利用可能にする
 *
 * 利用方法（フォントを適用する場合）:
 * アーティストのカスタムデザインでの利用が主のため、ArtistThemeProviderでの利用を想定する。（AppThemeProviderでも利用可能）
 * 1. CustomFontHeadsコンポーネントへ利用したいフォント名を配列で渡してフォントのheadタグへの挿入を行う。
 * 例： <CustomFontHeads customFonts={['roboto', 'instrumentSerif']} />
 * 2. 取得したFontオブジェクトのFontHeadコンポーネントをページの<head>内で利用することで、必要な<link>や<style>が挿入される。
 * 3. FontWrapperコンポーネントでテキストをラップすることで、該当フォントが適用される。
 * 4. コンポーネントでフォントを利用する場合はgetFontByNameで取得し、コンポーネントのclassNameに指定する。
 * 例： className={customDesign.itemCollectionFont ? getFontByName(customDesign.itemCollectionFont).className : ''}
 *
 * 利用方法（フォントを追加する場合）:
 * 1. CUSTOM_FONT_NAMESに新しいフォント名を追加する。カスタムデザインで使う場合、jsonに記載されているフォント名と合致させること。
 * 2. generateFont()で新しいフォントを生成する。生成方法が分岐する場合は、generateFont()内で吸収する。
 * 3. 生成したフォントをfontMapに追加すると、getFontByNameで新しいフォントを取得できるようになる。
 *
 * 備考：
 * このモジュールは、next13未満のバージョンでの利用を想定する。
 * next13以降はnext/google/fontsを利用することで、Google Fontsの最適化利用が可能である。
 */

import Head from 'next/head'
import React from 'react'

export const CUSTOM_FONT_NAMES = [
  'roboto',
  'instrumentSerif',
  'abhayaLibreMedium',
  'notoSansJp',
  'nunito',
  'zenMaruGothic',
  'poppins',
  'yujiSyuku',
  'marcellus',
] as const
export type CustomFontName = typeof CUSTOM_FONT_NAMES[number]

type FontOptions = {
  family: string
  weight: string | string[]
  subsets: string[]
  display: string
}

type Font = {
  url: string
  className: string
  fontFamily: string
  style: React.CSSProperties
  FontHead: React.FC
  FontWrapper: React.FC<
    React.PropsWithChildren<
      { as?: keyof JSX.IntrinsicElements } & React.HTMLAttributes<HTMLElement>
    >
  >
}

function makeGoogleFontUrl({
  family,
  weight = ['400'],
  subsets = ['latin'],
  display = 'swap',
}: FontOptions) {
  const familyParam = family.replace(/ /g, '+')
  const weights = Array.isArray(weight) ? weight.join(';') : weight
  const subsetStr = subsets.join(',')
  return `https://fonts.googleapis.com/css2?family=${familyParam}:wght@${weights}&display=${display}&subset=${subsetStr}`
}

function makeFontFamily(family: string) {
  if (family.toLowerCase().includes('serif')) return `'${family}', serif`
  if (family.toLowerCase().includes('sans')) return `'${family}', sans-serif`
  return `'${family}', sans-serif`
}

function makeClassName(options: FontOptions) {
  return `font-${options.family.replace(/\s+/g, '').toLowerCase()}`
}

function makeFontCss(
  className: string,
  fontFamily: string,
  weight: string | string[]
) {
  const weights = Array.isArray(weight) ? weight : [weight]
  return weights
    .map(
      w =>
        `.${className} { font-family: ${fontFamily} !important; font-weight: ${w}; }`
    )
    .join('\n')
}

function generateFont(options: FontOptions): Font {
  const url = makeGoogleFontUrl(options)
  const fontFamily = makeFontFamily(options.family)
  const className = makeClassName(options)
  const style = { fontFamily }
  const css = makeFontCss(className, fontFamily, options.weight)

  const FontHead: React.FC = () => {
    return (
      <Head>
        <link rel="stylesheet" href={url} />
        <style dangerouslySetInnerHTML={{ __html: css }} />
      </Head>
    )
  }

  function FontWrapper({
    children,
    as: Tag = 'span',
    className,
    style,
    ...rest
  }: React.PropsWithChildren<
    { as?: keyof JSX.IntrinsicElements } & React.HTMLAttributes<HTMLElement>
  >) {
    return (
      <>
        <FontHead />
        <Tag className={className} style={style} {...rest}>
          {children}
        </Tag>
      </>
    )
  }

  return {
    url,
    className,
    fontFamily,
    style,
    FontHead,
    FontWrapper,
  }
}

export const CustomFontHeads: React.FC<{ customFonts: CustomFontName[] }> = ({
  customFonts,
}) => (
  <>
    {customFonts.map((fontName: CustomFontName) => {
      const FontHead = getFontByName(fontName)?.FontHead
      return FontHead ? <FontHead key={fontName} /> : null
    })}
  </>
)

export const roboto = generateFont({
  family: 'Roboto',
  weight: ['400', '500', '700'],
  subsets: ['latin'],
  display: 'swap',
})

export const instrumentSerif = generateFont({
  family: 'Instrument Serif',
  weight: '400',
  subsets: ['latin'],
  display: 'swap',
})

export const abhayaLibreMedium = generateFont({
  family: 'Abhaya Libre',
  weight: '500',
  subsets: ['latin'],
  display: 'swap',
})

export const notoSansJp = generateFont({
  family: 'Noto Sans JP',
  weight: ['400', '500', '700'],
  subsets: ['latin'],
  display: 'swap',
})

export const nunito = generateFont({
  family: 'Nunito',
  weight: ['400', '700'],
  subsets: ['latin'],
  display: 'swap',
})

export const zenMaruGothic = generateFont({
  family: 'Zen Maru Gothic',
  weight: ['400', '500', '700'],
  subsets: ['latin'],
  display: 'swap',
})

export const poppins = generateFont({
  family: 'Poppins',
  weight: ['400', '500', '700'],
  subsets: ['latin'],
  display: 'swap',
})

export const yujiSyuku = generateFont({
  family: 'Yuji Syuku',
  weight: ['400'],
  subsets: ['latin'],
  display: 'swap',
})

export const marcellus = generateFont({
  family: 'Marcellus',
  weight: ['400'],
  subsets: ['latin'],
  display: 'swap',
})

const fontMap: Record<CustomFontName, Font> = {
  roboto,
  instrumentSerif,
  abhayaLibreMedium,
  notoSansJp,
  nunito,
  zenMaruGothic,
  poppins,
  yujiSyuku,
  marcellus,
}

// 存在しないフォント名が指定された場合はNoto Sans JPをデフォルトとする
const DEFAULT_FONT = notoSansJp

export const getFontByName = (name: CustomFontName): Font =>
  fontMap[name] ?? DEFAULT_FONT
